import React, { useState } from 'react';
import { formatRuntime, calculateSlotsNeeded } from '../../utils/movieUtils';
import { useTMDBMovie, useStreamingProviders } from '../../hooks/useTMDBData';
import { getEnhancedStreamingServices, convertTMDBProvidersToServices, TMDB_PROVIDER_MAPPING } from '../../services/streamingProviders';
import { buildPosterSrcSet, tmdbIdOf } from '../../services/tmdbService';
import StreamingServiceIcon from '../streaming/StreamingServiceIcon';
import { Play, ExternalLink, Users, Film, RefreshCw } from 'lucide-react';

const MovieModal = ({ 
  movie, 
  isVisible, 
  onClose, 
  isStarred, 
  onToggleStar,
  streamingServices = [],
  onUpdateStreamingServices 
}) => {
  const [selectedServices, setSelectedServices] = useState(movie?.streamingServices || []);
  const [preferredService, setPreferredService] = useState(movie?.preferredStreamingService || null);
  const [showMovieDetails, setShowMovieDetails] = useState(false);
  
  // Only fetch TMDB data for non-placeholder movies
  const { tmdbData, loading: tmdbLoading } = useTMDBMovie(
    movie?.isPlaceholder ? null : movie?.title,
    movie?.isPlaceholder ? null : movie?.year,
    movie?.isPlaceholder ? null : tmdbIdOf(movie)
  );
  const { providers, loading: providersLoading } = useStreamingProviders(
    movie?.isPlaceholder ? null : tmdbData?.id
  );

  const defaultStreamingServices = [
    { id: 'netflix', name: 'Netflix', color: 'bg-red-600', icon: '🎬' },
    { id: 'amazon', name: 'Amazon Prime', color: 'bg-blue-600', icon: '📺' },
    { id: 'disney', name: 'Disney+', color: 'bg-blue-800', icon: '🏰' },
    { id: 'hulu', name: 'Hulu', color: 'bg-green-600', icon: '🎭' },
    { id: 'hbo', name: 'HBO Max', color: 'bg-purple-700', icon: '👑' },
    { id: 'apple', name: 'Apple TV+', color: 'bg-gray-800', icon: '🍎' },
    { id: 'paramount', name: 'Paramount+', color: 'bg-blue-500', icon: '⭐' },
    { id: 'peacock', name: 'Peacock', color: 'bg-purple-600', icon: '🦚' },
    { id: 'showtime', name: 'Showtime', color: 'bg-red-800', icon: '🎪' },
    { id: 'starz', name: 'Starz', color: 'bg-black', icon: '✨' },
    { id: 'free', name: 'Free (Tubi/Crackle)', color: 'bg-green-700', icon: '🆓' },
    { id: 'rent', name: 'Digital Rental', color: 'bg-orange-700', icon: '💰' },
    { id: 'physical', name: 'Physical Media', color: 'bg-gray-700', icon: '💿' },
    { id: 'theater', name: 'In Theaters', color: 'bg-yellow-600', icon: '🎫' }
  ];

  // Get enhanced streaming services that includes TMDB providers
  const services = getEnhancedStreamingServices(
    providers, 
    streamingServices.length > 0 ? streamingServices : defaultStreamingServices
  );

  // Function to sync TMDB streaming providers with local services
  const syncWithTMDBProviders = () => {
    if (!providers) return;
    
    const tmdbServiceConversion = convertTMDBProvidersToServices(providers);
    const selectedServiceIds = tmdbServiceConversion.map(service => service.id);
    
    setSelectedServices(selectedServiceIds);
  };

  if (!isVisible || !movie) return null;

  const handleServiceToggle = (serviceId, isChecked) => {
    const newServices = isChecked
      ? [...selectedServices, serviceId]
      : selectedServices.filter(id => id !== serviceId);
    
    setSelectedServices(newServices);
    
    // If unchecking the preferred service, clear it
    if (!isChecked && preferredService === serviceId) {
      setPreferredService(null);
    }
  };

  const handlePreferredServiceChange = (serviceId) => {
    setPreferredService(serviceId);
  };

  const handleSave = () => {
    if (onUpdateStreamingServices) {
      onUpdateStreamingServices(movie.id, selectedServices, preferredService);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-purple-900 to-orange-900 px-6 py-4 border-b-2 border-orange-500">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-orange-100">🎬 Movie Details</h2>
            <button
              onClick={onClose}
              className="bg-red-600 hover:bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors"
            >
              ✕
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {/* Movie Info */}
          <div className="flex items-center gap-4 mb-6">
            {/* Movie Poster or Color Block */}
            {tmdbData?.posterUrl ? (
              <div className="relative">
                <img
                  src={tmdbData.posterUrl}
                  srcSet={buildPosterSrcSet(tmdbData.posterUrl, [92, 154, 185, 342])}
                  sizes="80px"
                  alt={`${movie.title} poster`}
                  className="w-20 h-30 object-cover rounded border-2 border-orange-600"
                  decoding="async"
                />
                {/* TMDB Rating Badge */}
                {tmdbData.rating && (
                  <div className="absolute -top-2 -right-2 bg-yellow-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                    ⭐ {tmdbData.rating.toFixed(1)}
                  </div>
                )}
              </div>
            ) : (
              <div className={`w-20 h-30 ${movie.color} rounded border-2 border-orange-600 flex items-center justify-center`}>
                <span className="text-white text-sm font-bold px-1 leading-tight text-center">
                  {movie.title.split(' ').map(word => word[0]).join('').slice(0, 3)}
                </span>
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-bold text-orange-100">{movie.title}</h3>
                {/* Trailer Button */}
                {tmdbData?.trailerUrl && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(tmdbData.trailerUrl, '_blank');
                    }}
                    className="bg-red-600 hover:bg-red-500 p-1 rounded-full transition-colors"
                    title="Watch Trailer"
                  >
                    <Play size={12} fill="white" />
                  </button>
                )}
              </div>
              
              <p className="text-orange-300 mb-1">
                Year: {tmdbData?.releaseDate ? new Date(tmdbData.releaseDate).getFullYear() : movie.year}
                {tmdbData?.genres && tmdbData.genres.length > 0 && (
                  <span> • {tmdbData.genres.slice(0, 2).join(', ')}</span>
                )}
              </p>
              <p className="text-orange-300 mb-1">
                Runtime: {formatRuntime(tmdbData?.runtime || movie.runtime)}
                {tmdbData?.rating && (
                  <span className="ml-2 text-yellow-400">⭐ {tmdbData.rating.toFixed(1)}</span>
                )}
              </p>
              <p className="text-orange-400 text-sm">
                {calculateSlotsNeeded(tmdbData?.runtime || movie.runtime)} hour slots needed
              </p>
              
              {/* Director and Stars */}
              {tmdbData && (tmdbData.director || tmdbData.stars?.length > 0) && (
                <div className="text-sm text-gray-300 mt-2">
                  {tmdbData.director && (
                    <div className="flex items-center gap-1">
                      <Users size={12} />
                      <span><strong>Director:</strong> {tmdbData.director}</span>
                    </div>
                  )}
                  {tmdbData.stars?.length > 0 && (
                    <div className="flex items-center gap-1">
                      <Film size={12} />
                      <span><strong>Stars:</strong> {tmdbData.stars.slice(0, 2).join(', ')}</span>
                    </div>
                  )}
                </div>
              )}
              
              {movie.letterboxdUri && (
                <div className="mt-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-600 text-white rounded text-xs">
                    <span>📝</span>
                    Imported from Letterboxd
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {/* Movie Details Toggle */}
          <div className="mb-6 flex gap-2">
            <button
              onClick={() => setShowMovieDetails(!showMovieDetails)}
              className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
            >
              <Film size={16} />
              {showMovieDetails ? 'Hide Details' : 'Show Details'}
            </button>
            
            {/* Sync with TMDB Button */}
            {providers && (providers.flatrate || providers.buy || providers.rent) && (
              <button
                onClick={syncWithTMDBProviders}
                disabled={providersLoading}
                className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
                title="Sync streaming services from TMDB"
              >
                <RefreshCw size={16} className={providersLoading ? 'animate-spin' : ''} />
                Sync Streaming
              </button>
            )}
          </div>

          {/* Expanded Movie Details */}
          {showMovieDetails && (
            <div className="bg-gray-700 rounded-lg p-4 mb-6">
              <h4 className="text-orange-300 font-bold mb-3">📽️ Movie Details</h4>
              
              {tmdbLoading && (
                <div className="text-gray-400 italic mb-3">Loading detailed movie information...</div>
              )}
              
              {/* Plot Summary */}
              {(tmdbData?.plot || movie?.plot) && (
                <div className="mb-4">
                  <h5 className="text-orange-200 font-semibold mb-2">Plot Summary:</h5>
                  <p className="text-gray-300 text-sm leading-relaxed">{tmdbData?.plot || movie?.plot}</p>
                </div>
              )}
              
              {/* Cast & Crew */}
              {((tmdbData && (tmdbData.director || tmdbData.writers?.length > 0 || tmdbData.stars?.length > 0)) || movie?.director) && (
                <div className="mb-4">
                  <h5 className="text-orange-200 font-semibold mb-2">Cast & Crew:</h5>
                  <div className="space-y-1 text-sm">
                    {(tmdbData?.director || movie?.director) && (
                      <div><strong className="text-orange-300">Director:</strong> {tmdbData?.director || movie?.director}</div>
                    )}
                    {tmdbData?.writers?.length > 0 && (
                      <div><strong className="text-orange-300">Writers:</strong> {tmdbData.writers.slice(0, 3).join(', ')}</div>
                    )}
                    {tmdbData?.stars?.length > 0 && (
                      <div><strong className="text-orange-300">Stars:</strong> {tmdbData.stars.slice(0, 5).join(', ')}</div>
                    )}
                  </div>
                </div>
              )}
              
              {/* TMDB Streaming Providers */}
              {providers && (providers.flatrate || providers.buy || providers.rent) && (
                <div className="mb-4">
                  <h5 className="text-orange-200 font-semibold mb-2">TMDB Streaming Data (US):</h5>
                  <div className="space-y-2">
                    {providers.flatrate && (
                      <div>
                        <span className="text-green-400 font-medium">Stream: </span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {providers.flatrate.map((provider) => {
                            const mappedService = TMDB_PROVIDER_MAPPING[provider.provider_id] || {
                              name: provider.provider_name,
                              logo: provider.provider_name,
                              color: 'bg-green-600'
                            };
                            return (
                              <div
                                key={provider.provider_id}
                                className="bg-green-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1"
                              >
                                <StreamingServiceIcon 
                                  service={mappedService} 
                                  size="sm" 
                                  showName={false}
                                />
                                <span>{provider.provider_name}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {providers.buy && (
                      <div>
                        <span className="text-blue-400 font-medium">Buy: </span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {providers.buy.map((provider) => {
                            const mappedService = TMDB_PROVIDER_MAPPING[provider.provider_id] || {
                              name: provider.provider_name,
                              logo: provider.provider_name,
                              color: 'bg-blue-600'
                            };
                            return (
                              <div
                                key={provider.provider_id}
                                className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1"
                              >
                                <StreamingServiceIcon 
                                  service={mappedService} 
                                  size="sm" 
                                  showName={false}
                                />
                                <span>{provider.provider_name}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {providers.rent && (
                      <div>
                        <span className="text-yellow-400 font-medium">Rent: </span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {providers.rent.map((provider) => {
                            const mappedService = TMDB_PROVIDER_MAPPING[provider.provider_id] || {
                              name: provider.provider_name,
                              logo: provider.provider_name,
                              color: 'bg-yellow-600'
                            };
                            return (
                              <div
                                key={provider.provider_id}
                                className="bg-yellow-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1"
                              >
                                <StreamingServiceIcon 
                                  service={mappedService} 
                                  size="sm" 
                                  showName={false}
                                />
                                <span>{provider.provider_name}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* External Links */}
              <div className="flex gap-2">
                {tmdbData?.id && (
                  <a
                    href={`https://www.themoviedb.org/movie/${tmdbData.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded text-sm transition-colors flex items-center gap-1"
                  >
                    <ExternalLink size={12} />
                    View on TMDB
                  </a>
                )}
                {tmdbData?.trailerUrl && (
                  <a
                    href={tmdbData.trailerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-red-600 hover:bg-red-500 text-white px-3 py-1 rounded text-sm transition-colors flex items-center gap-1"
                  >
                    <Play size={12} />
                    Watch Trailer
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Streaming Services */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-lg font-bold text-orange-300">📺 Streaming Availability</h4>
              {providers && (providers.flatrate || providers.buy || providers.rent) && (
                <span className="text-xs text-gray-400">
                  💡 Use "Sync Streaming" button above to auto-populate from TMDB
                </span>
              )}
            </div>
            
            <div className="space-y-2 mb-4">
              {services.map(service => {
                const isSelected = selectedServices.includes(service.id);
                const isPreferred = preferredService === service.id;
                
                return (
                  <div key={service.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`service-${service.id}`}
                        checked={isSelected}
                        onChange={(e) => handleServiceToggle(service.id, e.target.checked)}
                        className="rounded border-orange-500 text-orange-600 focus:ring-orange-500"
                      />
                      <label 
                        htmlFor={`service-${service.id}`}
                        className={`flex items-center gap-2 px-3 py-1 rounded text-sm text-white cursor-pointer ${service.color}`}
                      >
                        <StreamingServiceIcon 
                          service={service} 
                          size="sm" 
                          showName={true}
                        />
                      </label>
                    </div>
                    
                    {isSelected && (
                      <button
                        onClick={() => handlePreferredServiceChange(service.id)}
                        className={`px-2 py-1 rounded text-xs font-bold transition-colors ${
                          isPreferred
                            ? 'bg-yellow-600 text-white'
                            : 'bg-gray-600 text-orange-300 hover:bg-gray-500'
                        }`}
                      >
                        {isPreferred ? '⭐ Preferred' : 'Set Preferred'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            
            {selectedServices.length === 0 && (
              <div className="text-center py-4 text-orange-400 bg-gray-900 rounded-lg border border-orange-600">
                <p className="text-sm">No streaming services selected</p>
                <p className="text-xs">Check the boxes above to add streaming options</p>
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg font-bold transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onToggleStar(movie.id);
                // Don't close modal, just update the star status
              }}
              className={`px-4 py-2 rounded-lg font-bold transition-colors ${
                isStarred
                  ? 'bg-red-600 hover:bg-red-500 text-white'
                  : 'bg-purple-600 hover:bg-purple-500 text-white'
              }`}
            >
              {isStarred ? '⚰️ Remove from Starred' : '⚰️ Add to Starred'}
            </button>
            <button
              onClick={handleSave}
              className="bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded-lg font-bold transition-colors"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovieModal;

